import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface ComboboxProps<T> {
  options: T[];
  value: string;
  onChange: (key: string, option: T | null) => void;
  getKey: (option: T) => string;
  getLabel: (option: T) => string;
  getGroup?: (option: T) => string;
  renderOption?: (option: T) => React.ReactNode;
  searchKeys?: (option: T) => string[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  customOption?: { label: string; onSelect: () => void };
  disabled?: boolean;
  triggerTestId?: string;
  itemTestIdPrefix?: string;
  contentClassName?: string;
  triggerClassName?: string;
  triggerLabel?: React.ReactNode;
}

export function Combobox<T>({
  options,
  value,
  onChange,
  getKey,
  getLabel,
  getGroup,
  renderOption,
  searchKeys,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No results.",
  customOption,
  disabled,
  triggerTestId,
  itemTestIdPrefix,
  contentClassName,
  triggerClassName,
  triggerLabel,
}: ComboboxProps<T>) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((o) => getKey(o) === value) ?? null;

  const grouped = React.useMemo(() => {
    if (!getGroup) return null;
    const map = new Map<string, T[]>();
    for (const opt of options) {
      const g = getGroup(opt);
      const arr = map.get(g) ?? [];
      arr.push(opt);
      map.set(g, arr);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [options, getGroup]);

  const display = triggerLabel ?? (selected ? getLabel(selected) : value || placeholder);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !selected && !value && "text-muted-foreground",
            triggerClassName,
          )}
          data-testid={triggerTestId}
        >
          <span className="truncate text-left">{display}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-[--radix-popover-trigger-width] p-0", contentClassName)}
        align="start"
      >
        <Command
          filter={(itemValue, search) => {
            // itemValue is the option key; we look up the option's
            // searchable strings (label + extra keys) and match all
            // search tokens (case-insensitive) against the joined
            // haystack so multi-word search ("animal detail") works.
            const opt = options.find((o) => getKey(o) === itemValue);
            if (!opt) {
              // Allow custom-option entry to surface for any non-empty
              // search.
              return itemValue === "__custom__" ? 1 : 0;
            }
            const hay = [getLabel(opt), ...(searchKeys?.(opt) ?? [])]
              .join(" ")
              .toLowerCase();
            const tokens = search.toLowerCase().split(/\s+/).filter(Boolean);
            if (tokens.length === 0) return 1;
            return tokens.every((t) => hay.includes(t)) ? 1 : 0;
          }}
        >
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {grouped
              ? grouped.map(([groupName, opts]) => (
                  <CommandGroup key={groupName} heading={groupName}>
                    {opts.map((opt) => {
                      const k = getKey(opt);
                      return (
                        <CommandItem
                          key={k}
                          value={k}
                          onSelect={() => {
                            onChange(k, opt);
                            setOpen(false);
                          }}
                          data-testid={
                            itemTestIdPrefix
                              ? `${itemTestIdPrefix}-${k.replace(/[^a-zA-Z0-9-]/g, "-")}`
                              : undefined
                          }
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              value === k ? "opacity-100" : "opacity-0",
                            )}
                          />
                          {renderOption ? renderOption(opt) : getLabel(opt)}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))
              : (
                <CommandGroup>
                  {options.map((opt) => {
                    const k = getKey(opt);
                    return (
                      <CommandItem
                        key={k}
                        value={k}
                        onSelect={() => {
                          onChange(k, opt);
                          setOpen(false);
                        }}
                        data-testid={
                          itemTestIdPrefix
                            ? `${itemTestIdPrefix}-${k.replace(/[^a-zA-Z0-9-]/g, "-")}`
                            : undefined
                        }
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === k ? "opacity-100" : "opacity-0",
                          )}
                        />
                        {renderOption ? renderOption(opt) : getLabel(opt)}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            {customOption && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    value="__custom__"
                    onSelect={() => {
                      customOption.onSelect();
                      setOpen(false);
                    }}
                    data-testid={
                      itemTestIdPrefix ? `${itemTestIdPrefix}-custom` : undefined
                    }
                  >
                    {customOption.label}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
